import { Observable, Subscription } from 'rxjs';

import { SharedSolaceStreamService } from 'src/solace';

export enum ForeignExchangesIn {
  MXN = 'mxn',
  USD = 'usd',
  NONE = '',
}

export enum ForeignExchanges {
  PESO_MXN = 'PESO MXN',
  EURO = 'EURO',
  POUND = 'POUND',
  YEN = 'YEN',
  REAL = 'REAL',
  CANADA_DLL = 'CANADA DLL',
  RENMINBI_CHINO = 'RENMINBI CHINO',
  CHILE = 'CHILE',
  LIRA_TURCA = 'LIRA TURCA',
  RUBLO_RUSO = 'RUBLO RUSO',
  RUPIAH_INDONESIA = 'RUPIAH INDONESIA',
  RUPEE_INDIA = 'RUPEE INDIA',
  PESO_COLOMBIANO = 'PESO COLOMBIANO',
  ARGENTINA = 'ARGENTINA',
}

export class ForeignExchangeRow {
  name: string;
  lastValue: number;
  unitChange: number;
  percentageChange: number;
  volume: number;
  closeValue: number;
  isPlaceholder: boolean;

  private consumerId = 'currency';
  private marketName = 'currencies';
  private foreignExchangeSubscription: Subscription;

  constructor(
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private index: string,
    private exchangeReference?: ForeignExchangeRow,
    private foreignexchange?: string,
  ) {
    const marketObservable$: any =
      this.sharedSolaceStreamService.getObservableSymbol(
        this.marketName,
        index,
        this.consumerId,
      );

    const lastIndex: any = this.sharedSolaceStreamService.getMarketElement(
      'currencies',
      index,
    );

    this.setCurrentValues(lastIndex);

    this.foreignExchangeSubscription = marketObservable$.subscribe(
      (indexShadowed: any) => {
        this.setCurrentValues(indexShadowed);
      },
    );
  }

  dispose(): any {
    if (this.foreignExchangeSubscription) {
      this.foreignExchangeSubscription.unsubscribe();
      this.sharedSolaceStreamService.removeConsumerFromObservable(
        this.name,
        this.marketName,
        this.consumerId,
      );
    }
  }

  get calculatedValue() {
    if (!this.exchangeReference) {
      throw new Error('Missing exchange reference initialization');
    }

    if (this.foreignexchange === ForeignExchangesIn.MXN) {
      if (this.index === ForeignExchanges.PESO_MXN) {
        return this.exchangeReference.lastValue;
      } else if (
        this.index === ForeignExchanges.EURO ||
        this.index === ForeignExchanges.POUND ||
        this.index === ForeignExchanges.YEN
      ) {
        return this.exchangeReference.lastValue * this.lastValue;
      } else {
        return this.exchangeReference.lastValue / this.lastValue;
      }
    } else if (this.foreignexchange === ForeignExchangesIn.USD) {
      if (this.index === ForeignExchanges.PESO_MXN) {
        return 1 / this.exchangeReference.lastValue;
      } else if (
        this.index === ForeignExchanges.EURO ||
        this.index === ForeignExchanges.POUND ||
        this.index === ForeignExchanges.YEN
      ) {
        return this.lastValue;
      } else {
        return (
          this.exchangeReference.lastValue /
          this.lastValue /
          this.exchangeReference.lastValue
        );
      }
    } else {
      if (this.index === ForeignExchanges.PESO_MXN) {
        return this.lastValue;
      } else if (
        this.index === ForeignExchanges.EURO ||
        this.index === ForeignExchanges.POUND ||
        this.index === ForeignExchanges.YEN
      ) {
        return this.exchangeReference.lastValue * this.lastValue;
      }
    }
  }

  get calculatedCloseValue() {
    if (!this.exchangeReference) {
      throw new Error('Missing exchange reference initialization');
    }

    if (this.foreignexchange === ForeignExchangesIn.MXN) {
      if (this.index === ForeignExchanges.PESO_MXN) {
        return this.closeValue;
      } else if (
        this.index === ForeignExchanges.EURO ||
        this.index === ForeignExchanges.POUND ||
        this.index === ForeignExchanges.YEN
      ) {
        return this.exchangeReference.closeValue * this.closeValue;
      } else {
        return this.exchangeReference.closeValue / this.closeValue;
      }
    } else if (this.foreignexchange === ForeignExchangesIn.USD) {
      if (this.index === ForeignExchanges.PESO_MXN) {
        return 1 / this.closeValue;
      } else if (
        this.index === ForeignExchanges.EURO ||
        this.index === ForeignExchanges.POUND ||
        this.index === ForeignExchanges.YEN
      ) {
        return this.closeValue;
      } else {
        return (
          this.exchangeReference.lastValue /
          this.closeValue /
          this.exchangeReference.lastValue
        );
      }
    }
  }

  get calculatedUnitChange() {
    if (!this.exchangeReference) {
      throw new Error('Missing exchange reference initialization');
    }

    if (
      this.foreignexchange === ForeignExchangesIn.MXN ||
      this.foreignexchange === ForeignExchangesIn.USD
    ) {
      if (this.closeValue !== undefined) {
        const val: number = this.calculatedValue - this.calculatedCloseValue;
        return Number(val).toFixed(7);
      } else {
        const val: number = this.unitChange;
        return Number(val).toFixed(7);
      }
    }
  }

  get calculatedPercentageChange() {
    if (!this.exchangeReference) {
      throw new Error('Missing exchange reference initialization');
    }

    if (
      this.foreignexchange === ForeignExchangesIn.MXN ||
      this.foreignexchange === ForeignExchangesIn.USD
    ) {
      if (this.closeValue !== undefined) {
        const val: number =
          this.calculatedValue / this.calculatedCloseValue - 1;
        return Number(val);
      } else {
        return Number(this.percentageChange) / 100;
      }
    }
  }

  private setCurrentValues(index: any) {
    if (index === undefined) {
      index = {
        name: '',
        lastValue: 0,
        unitChange: 0,
        percentageChange: 0,
        volume: 0,
      };
      this.isPlaceholder = true;
    } else {
      this.isPlaceholder = false;
    }

    this.name = index.name;
    this.lastValue = index.lastValue;
    this.unitChange = index.unitChange;
    this.percentageChange = index.percentageChange;
    this.volume = index.volume;
    this.closeValue = index.closeValue;
  }
}
